// =====================================================================
// Shared Supabase queries + period helpers for tutor payouts (admin-only).
// Pure money math lives in ./payout — this module is just data plumbing
// shared by the Tutors page and the payout invoice page.
// =====================================================================

import { startOfMonth, endOfMonth, subMonths } from 'date-fns'
import { supabase } from './supabase'
import { PayableSessionLike } from './payout'

export type PayoutSessionRow = PayableSessionLike & { tutor_id: string }
export type OwedPeriod = 'month' | 'lastMonth' | 'all' | 'custom'

/** Custom date bounds as 'yyyy-MM-dd' input values; either side may be empty. */
export type CustomRange = { from?: string | null; to?: string | null }

export const OWED_PERIOD_LABELS: Record<OwedPeriod, string> = {
  month: 'This month',
  lastMonth: 'Last month',
  all: 'All time',
  custom: 'Custom',
}

export function periodRange(period: OwedPeriod, custom?: CustomRange): { from: Date | null; to: Date | null } {
  const now = new Date()
  if (period === 'month') return { from: startOfMonth(now), to: null }
  if (period === 'lastMonth') {
    const prev = subMonths(now, 1)
    return { from: startOfMonth(prev), to: endOfMonth(prev) }
  }
  if (period === 'custom') {
    return { from: parseDay(custom?.from, 'T00:00:00'), to: parseDay(custom?.to, 'T23:59:59') }
  }
  return { from: null, to: null }
}

/** Parse a 'yyyy-MM-dd' bound; invalid or empty input (e.g. a hand-edited URL) means "no bound". */
function parseDay(day: string | null | undefined, time: string): Date | null {
  if (!day) return null
  const d = new Date(day + time)
  return isNaN(d.getTime()) ? null : d
}

export function filterToPeriod<T extends { started_at: string }>(
  sessions: readonly T[],
  period: OwedPeriod,
  custom?: CustomRange
): T[] {
  const { from, to } = periodRange(period, custom)
  return sessions.filter((s) => {
    const d = new Date(s.started_at)
    if (from && d < from) return false
    if (to && d > to) return false
    return true
  })
}

/** Fetch every payable session, paging past Supabase's 1000-row cap. */
export async function fetchAllPayoutSessions(): Promise<PayoutSessionRow[]> {
  const pageSize = 1000
  const rows: PayoutSessionRow[] = []
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await supabase
      .from('sessions')
      .select('tutor_id, student_id, started_at, minutes, approval_status, lifecycle_status, is_free')
      .eq('approval_status', 'approved')
      .eq('lifecycle_status', 'completed')
      // id tiebreaker: started_at alone is non-unique, and unstable ordering
      // across pages could duplicate/drop rows at page boundaries.
      .order('started_at', { ascending: false })
      .order('id', { ascending: true })
      .range(from, from + pageSize - 1)
    if (error) throw error
    rows.push(...((data as PayoutSessionRow[]) || []))
    if (!data || data.length < pageSize) return rows
  }
}
